import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { auth } from '../lib/firebase';
import { Mail, RefreshCw, LogOut, ArrowRight, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';

const VerifyEmail = () => {
  const { currentUser, logout, sendVerificationEmail, reloadUser } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(true); // Start disabled since email was just sent
  const [resendCountdown, setResendCountdown] = useState(60); // Start with 60 second cooldown (email was just sent)
  const [checkDisabled, setCheckDisabled] = useState(false);
  const [checkCountdown, setCheckCountdown] = useState(0);

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
    } else if (currentUser.emailVerified) {
      navigate('/dashboard');
    }
  }, [currentUser, navigate]);

  // Countdown timer for resend email button
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setResendDisabled(false);
    }
  }, [resendCountdown]);

  // Countdown timer for check verification button
  useEffect(() => {
    if (checkCountdown > 0) {
      const timer = setTimeout(() => setCheckCountdown(checkCountdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCheckDisabled(false);
    }
  }, [checkCountdown]);

  const handleResendEmail = async () => {
    try {
      setResendDisabled(true);
      await sendVerificationEmail();
      // On success, set 60 second cooldown
      setResendCountdown(60);
    } catch (error: any) {
      // Check if it's a rate limit error (Firebase typically returns auth/too-many-requests)
      // or if the error message indicates rate limiting
      const isRateLimitError = error?.code === 'auth/too-many-requests' || 
                               error?.message?.toLowerCase().includes('too many') ||
                               error?.message?.toLowerCase().includes('rate limit');
      
      if (isRateLimitError) {
        // Keep the cooldown active - don't reset it
        // Set a longer cooldown (2 minutes) if rate limited
        setResendCountdown(Math.max(resendCountdown, 120));
      } else {
        // For other errors (network, etc.), allow retry but with a shorter cooldown
        setResendCountdown(30);
      }
      // Don't reset resendDisabled here - let the countdown handle it
    }
  };

  const handleVerificationCheck = async () => {
    if (checkDisabled) return;
    
    setIsLoading(true);
    setCheckDisabled(true);
    setCheckCountdown(10); // 10 second cooldown to prevent spam clicking
    
    try {
      await reloadUser();
      // Use auth.currentUser directly to get the most up-to-date verification status
      // React state might not be updated immediately after reloadUser()
      const updatedUser = auth.currentUser;
      if (updatedUser?.emailVerified) {
        navigate('/dashboard');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to logout:', error);
    }
  };

  if (!currentUser) return null;

  return (
    <motion.div 
      className="min-h-screen flex items-center justify-center bg-gray-50 px-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full space-y-8"
      >
        <div className="text-center">
          <div className="mx-auto w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
            <Mail className="h-8 w-8 text-primary-600" />
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900">Verify your email</h2>
          <p className="mt-2 text-sm text-gray-600">
            We've sent a verification email to{' '}
            <span className="font-medium text-primary-600">{currentUser.email}</span>
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 space-y-4">
          <div className="text-sm text-gray-600">
            <p>Please check your email and click the verification link to activate your account.</p>
            <p className="mt-2">
              If you don't see the email, check your spam folder or request a new verification link.
            </p>
          </div>

          <div className="space-y-3">
            <button
              onClick={handleVerificationCheck}
              disabled={isLoading || checkDisabled}
              className="w-full flex items-center justify-center px-4 py-2 border border-transparent text-sm rounded-md text-white bg-primary-600 hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Verifying...
                </>
              ) : (
                <>
                  <RefreshCw className="h-5 w-5 mr-2" />
                  {checkDisabled 
                    ? `Check again in ${checkCountdown}s` 
                    : 'Check verification status'}
                </>
              )}
            </button>

            <button
              onClick={handleResendEmail}
              disabled={resendDisabled}
              className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 text-sm rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              <Mail className="h-5 w-5 mr-2" />
              {resendDisabled 
                ? `Resend available in ${resendCountdown}s` 
                : 'Resend verification email'}
            </button>

            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 text-sm rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 font-medium"
            >
              <LogOut className="h-5 w-5 mr-2" />
              Sign out
            </button>
          </div>
        </div>

        <div className="text-center">
          <Link
            to="/login"
            className="inline-flex items-center text-sm text-primary-600 hover:text-primary-500"
          >
            Back to login
            <ArrowRight className="h-4 w-4 ml-1" />
          </Link>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default VerifyEmail;